import React, { useState } from 'react';
import { MessageCircle, X, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const ChatBox = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    topic: '',
    message: ''
  });
  const [showAlert, setShowAlert] = useState(null);
  const navigate = useNavigate();

  const topics = [
    { value: 'medicare', label: 'Medicare Questions', path: '/medicare-explained' },
    { value: 'turning-65', label: 'Turning 65', path: '/turning-65' },
    { value: 'individual', label: 'Individual/Family Insurance', path: '/individual-family' },
    { value: 'dental', label: 'Dental & Vision', path: '/dental' },
    { value: 'group', label: 'Group Insurance', path: '/group-insurance' },
    { value: 'general', label: 'General Inquiry', path: null }
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock submission - will be replaced with actual backend call
    console.log('Form submitted:', formData);
    
    setShowAlert({
      type: 'success',
      message: 'Message sent successfully! We\'ll get back to you soon.'
    });
    
    setFormData({ name: '', email: '', phone: '', topic: '', message: '' });
    
    setTimeout(() => {
      setShowAlert(null);
      setIsOpen(false);
    }, 3000);
  };

  const handleTopicChange = (value) => {
    setFormData({ ...formData, topic: value });
    const selectedTopic = topics.find(t => t.value === value);
    
    // Only navigate if it's NOT a general inquiry and user hasn't filled the form
    if (selectedTopic?.path && !formData.message) {
      setIsOpen(false);
      navigate(selectedTopic.path);
    }
  };

  const closeAlert = () => {
    setShowAlert(null);
  };

  return (
    <>
      {/* Alert Overlay */}
      {showAlert && (
        <div className="fixed top-4 right-4 z-[60] animate-in slide-in-from-top">
          <Card className={`w-80 shadow-2xl border-2 ${
            showAlert.type === 'success' ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'
          }`}>
            <CardContent className="p-4 flex items-start gap-3">
              {showAlert.type === 'success' ? (
                <CheckCircle className="text-green-600 flex-shrink-0" size={24} />
              ) : (
                <AlertCircle className="text-red-600 flex-shrink-0" size={24} />
              )}
              <div className="flex-1">
                <p className={`font-semibold ${
                  showAlert.type === 'success' ? 'text-green-900' : 'text-red-900'
                }`}>{showAlert.message}</p>
              </div>
              <button
                onClick={closeAlert}
                className="text-gray-500 hover:text-gray-700 transition-colors"
                aria-label="Close alert"
              >
                <X size={18} />
              </button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 bg-teal-600 hover:bg-teal-700 text-white rounded-full p-4 shadow-lg transition-all transform hover:scale-110 z-50"
          aria-label="Open chat"
        >
          <MessageCircle size={24} />
        </button>
      )}

      {/* Chat Box */}
      {isOpen && (
        <Card className="fixed bottom-6 right-6 w-96 max-w-[calc(100vw-2rem)] shadow-2xl z-50 animate-in slide-in-from-bottom">
          <CardHeader className="bg-teal-600 text-white rounded-t-lg">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Contact Us</CardTitle>
              <button
                onClick={() => setIsOpen(false)}
                className="hover:bg-teal-700 rounded-full p-1 transition-colors"
                aria-label="Close chat"
              >
                <X size={20} />
              </button>
            </div>
          </CardHeader>
          <CardContent className="p-4 max-h-[60vh] overflow-y-auto">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Name *</label>
                <Input
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Your name"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Email *</label>
                <Input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Phone</label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="(734) 209-6712"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Topic</label>
                <Select value={formData.topic} onValueChange={handleTopicChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a topic" />
                  </SelectTrigger>
                  <SelectContent>
                    {topics.map((topic) => (
                      <SelectItem key={topic.value} value={topic.value}>
                        {topic.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formData.topic && formData.topic !== 'general' && (
                  <p className="text-xs text-gray-500 mt-1">
                    Or continue filling this form to send us a message
                  </p>
                )}
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Message *</label>
                <Textarea
                  required
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="How can we help you?"
                  rows={4}
                />
              </div>

              <Button type="submit" className="w-full bg-teal-600 hover:bg-teal-700 text-white">
                <Send className="mr-2" size={16} />
                Send Message
              </Button>
            </form>
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default ChatBox;